import React, { useEffect, useMemo, useState } from "react";
import {
  FiBookOpen,
  FiChevronDown,
  FiMail,
  FiMapPin,
  FiPhone,
  FiSearch,
  FiSettings,
  FiUser,
  FiUserPlus,
  FiUsers,
  FiX,
} from "react-icons/fi";
import EmeraldSwitch from "../../../../components/EmeraldSwitch";
import http from "../../../../api/apiClient";

const TextLink = ({ onClick, children, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="text-sm font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline disabled:opacity-50 dark:text-emerald-300 dark:hover:text-emerald-200"
  >
    {children}
  </button>
);

const escapeVCardValue = (value) =>
  typeof value === "string"
    ? value.replace(/\r?\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/:/g, "\\:")
    : "";

const normalizePhone = (value) => (typeof value === "string" ? value.replace(/\s+/g, "") : "");

const splitDisplayName = (displayName) => {
  if (!displayName) return { firstName: "", lastName: "" };
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts.pop();
  return { firstName: parts.join(" "), lastName };
};

const buildVCardContent = (card, opts = {}) => {
  const { contextChildName } = opts;
  const formattedName =
    (card.displayName && card.displayName.trim()) ||
    [card.firstName, card.lastName].filter(Boolean).join(" ").trim();
  const { firstName, lastName } = splitDisplayName(formattedName);
  const lines = ["BEGIN:VCARD", "VERSION:3.0"];
  const safeFN = escapeVCardValue(formattedName || card.email || "Contact");

  lines.push(`FN:${safeFN}`);
  lines.push(`N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`);

  if (card.email) lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(card.email)}`);
  if (card.phoneMobile) lines.push(`TEL;TYPE=CELL,VOICE:${escapeVCardValue(normalizePhone(card.phoneMobile))}`);
  if (card.phoneOther) lines.push(`TEL;TYPE=VOICE:${escapeVCardValue(normalizePhone(card.phoneOther))}`);

  if (card.addressStreet || card.addressPostalCode || card.addressCity) {
    const street = escapeVCardValue(card.addressStreet || "");
    const city = escapeVCardValue(card.addressCity || "");
    const postal = escapeVCardValue(card.addressPostalCode || "");
    lines.push(`ADR;TYPE=HOME:${["", "", street, city, "", postal, ""].join(";")}`);
  }

  if (contextChildName) {
    const child = escapeVCardValue(contextChildName);
    lines.push(`NOTE:${escapeVCardValue(`Linked child - ${child}`)}`);
    lines.push(`item1.X-ABRELATEDNAMES:${child}`);
    lines.push(`item1.X-ABLabel:_$!<Child>!$_`);
  }

  if (Array.isArray(card.matchedChildren) && card.matchedChildren.length > 0) {
    const childrenNames = card.matchedChildren
      .map((kid) => (typeof kid.name === "string" ? kid.name : ""))
      .filter(Boolean)
      .join(", ");
    if (childrenNames) lines.push(`NOTE:${escapeVCardValue(`Children - ${childrenNames}`)}`);
  }

  lines.push("END:VCARD");
  return lines.join("\r\n");
};

const buildFileName = (displayName) => {
  const base = (displayName || "contact").trim().toLowerCase();
  const safe = base.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
  return `${safe || "contact"}.vcf`;
};

const getInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const getAddressText = (card) => {
  if (card?.address) return card.address;
  return [card?.addressStreet, [card?.addressPostalCode, card?.addressCity].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
};

const normalizeParentContacts = (item) => {
  const rawParents =
    (Array.isArray(item?.parents) && item.parents) ||
    (Array.isArray(item?.parentContacts) && item.parentContacts) ||
    (Array.isArray(item?.relatedParents) && item.relatedParents) ||
    [];

  if (rawParents.length > 0) {
    return rawParents
      .map((parent) => {
        const displayName =
          parent?.displayName ||
          parent?.name ||
          [parent?.firstName, parent?.lastName].filter(Boolean).join(" ").trim() ||
          "";
        return {
          ...parent,
          type: "PARENT",
          id: parent?.id || parent?.parentId,
          parentId: parent?.parentId || parent?.id,
          displayName,
          initials: parent?.initials || getInitials(displayName),
          email: parent?.email || "",
          phoneMobile: parent?.phone || parent?.phoneMobile || "",
          address: parent?.address || getAddressText(parent),
          availableActions: Array.isArray(parent?.availableActions)
            ? parent.availableActions.map((action) => String(action).toUpperCase())
            : null,
        };
      })
      .filter((parent) => parent.displayName);
  }

  if (Array.isArray(item?.parentNames)) {
    return item.parentNames.filter(Boolean).map((name, index) => ({
      type: "PARENT",
      id: `parent-name-${index}`,
      parentId: `parent-name-${index}`,
      displayName: name,
      initials: getInitials(name),
      email: "",
      phoneMobile: "",
      address: "",
      availableActions: [],
      isNameOnly: true,
    }));
  }

  return [];
};

const normalizeDirectoryResult = (item, t) => {
  const type = String(item?.type || item?.resultType || "PARENT").toUpperCase();
  const displayName =
    item?.displayName ||
    item?.name ||
    [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim() ||
    t("common.unknown", { defaultValue: "Unknown" });

  return {
    ...item,
    type,
    displayName,
    initials: item?.initials || getInitials(displayName),
    parentId: item?.parentId || item?.id,
    childId: item?.childId || item?.id,
    email: item?.email || "",
    phoneMobile: item?.phone || item?.phoneMobile || "",
    address: item?.address || getAddressText(item),
    classroomName: item?.classroomName || item?.classroom || "",
    parents: normalizeParentContacts(item),
    parentNames: Array.isArray(item?.parentNames) ? item.parentNames.filter(Boolean) : [],
    availableActions: Array.isArray(item?.availableActions)
      ? item.availableActions.map((action) => String(action).toUpperCase())
      : null,
  };
};

const extractResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const iconButtonClass =
  "inline-flex h-9 w-9 items-center justify-center rounded-2xl border border-emerald-100 bg-white text-emerald-700 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-500/30 dark:bg-slate-900/80 dark:text-emerald-300 dark:hover:bg-slate-800";

const DirectoryTab = ({
  t,
  canViewDirectory,
  openDirSettings,
  dirSettingsOpen,
  setDirSettingsOpen,
  saveDirectorySettings,
  turnOffListing,
  myDirErr,
  myDirSaving,
  dirQ,
  setDirQ,
  dirData,
  dirLoading,
  setDirData,
  dirInputRef,
  setDirPage,
  share,
  setShare,
  previewValues,
}) => {
  const [expandedContactKey, setExpandedContactKey] = useState(null);
  const [unifiedData, setUnifiedData] = useState(dirData || null);
  const [unifiedLoading, setUnifiedLoading] = useState(false);
  const [unifiedError, setUnifiedError] = useState(null);

  const directoryTitle = t("parentPortal.directory.title", { defaultValue: "Directory" });
  const searchPlaceholder = t("parentPortal.directory.search_placeholder", {
    defaultValue: "Search parents, children or teachers...",
  });

  const content = useMemo(
    () => extractResults(unifiedData).map((item) => normalizeDirectoryResult(item, t)),
    [unifiedData, t]
  );
  const hasResults = content.length > 0;
  const totalElements = Number.isFinite(unifiedData?.totalElements)
    ? unifiedData.totalElements
    : Number.isFinite(unifiedData?.total)
      ? unifiedData.total
      : content.length;
  const dirIsLoading = dirLoading || unifiedLoading;

  useEffect(() => {
    setUnifiedData(dirData || null);
  }, [dirData]);

  useEffect(() => {
    if (!canViewDirectory) return undefined;

    setExpandedContactKey(null);
    setUnifiedError(null);

    const q = dirQ.trim();
    if (!q) {
      setUnifiedData(null);
      setDirData?.(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setDirPage?.(0);
      setUnifiedLoading(true);
      try {
        const response = await http.get("/parent-portal/directory/search", {
          params: { q, limit: 25 },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          const payload = response?.data ?? [];
          setUnifiedData(payload);
          setDirData?.(payload);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setUnifiedData({ content: [] });
          setUnifiedError(
            error?.response?.data?.message ||
              t("parentPortal.directory.search_failed", { defaultValue: "Searching the directory failed." })
          );
        }
      } finally {
        if (!controller.signal.aborted) setUnifiedLoading(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [canViewDirectory, dirQ, setDirData, setDirPage, t]);

  const submitDirectorySearch = async () => {
    const q = dirQ.trim();
    if (!q) {
      setUnifiedData(null);
      setDirData?.(null);
      return;
    }

    setExpandedContactKey(null);
    setUnifiedError(null);
    setDirPage?.(0);
    setUnifiedLoading(true);
    try {
      const response = await http.get("/parent-portal/directory/search", { params: { q, limit: 25 } });
      const payload = response?.data ?? [];
      setUnifiedData(payload);
      setDirData?.(payload);
    } catch (error) {
      setUnifiedData({ content: [] });
      setUnifiedError(
        error?.response?.data?.message ||
          t("parentPortal.directory.search_failed", { defaultValue: "Searching the directory failed." })
      );
    } finally {
      setUnifiedLoading(false);
    }
  };

  const openMap = (raw) => {
    if (!raw || typeof window === "undefined") return;
    const query = encodeURIComponent(raw);
    const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      window.location.href = `http://maps.apple.com/?q=${query}`;
      return;
    }

    if (isAndroid) {
      window.location.href = `geo:0,0?q=${query}`;
      return;
    }

    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank", "noopener,noreferrer");
  };

  const downloadVCard = async (card, opts = {}) => {
    if (typeof window === "undefined") return;

    const contentValue = buildVCardContent(card, opts);
    const fileName = buildFileName(card.displayName);
    const blob = new Blob([contentValue], { type: "text/vcard;charset=utf-8" });
    const ua = navigator.userAgent || "";
    // iPadOS 13+ reports itself as "Macintosh"; every iOS browser is WebKit-based.
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    const file = new File([blob], fileName, { type: "text/vcard" });

    if (!isIOS && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: "Contact", text: "Add to contacts" });
        return;
      } catch {
        // fallback below
      }
    }

    if (isIOS) {
      // iOS Safari blocks top-level navigation to data: URLs, so the old data: URL did nothing.
      // Opening a blob URL in the same tab makes iOS show its "Create New Contact" sheet.
      const url = URL.createObjectURL(blob);
      window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildContactKey = (card, index) =>
    `${card.type || "RESULT"}-${card.parentId || card.childId || card.id || card.displayName || "contact"}-${index}`;

  const renderActionButtons = (card, opts = {}) => {
    const { contextChildName } = opts;
    const addressText = getAddressText(card);
    const availableActions = Array.isArray(card.availableActions) ? card.availableActions : null;
    const actionAllowed = (action, fallback) => (availableActions ? availableActions.includes(action) : fallback);
    const hasEmail = !!card.email;
    const hasPhone = !!card.phoneMobile;
    const hasAddress = !!addressText;
    const canAdd = actionAllowed("ADD", hasEmail || hasPhone || hasAddress || !!card.displayName);

    return (
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {actionAllowed("MAIL", hasEmail) && (
          <a href={`mailto:${card.email}`} className={iconButtonClass} aria-label={t("parentPortal.directory.email_parent", { defaultValue: "Mail" })}>
            <FiMail className="h-4 w-4" />
          </a>
        )}
        {actionAllowed("CALL", hasPhone) && (
          <a href={`tel:${card.phoneMobile.replace(/\s+/g, "")}`} className={iconButtonClass} aria-label={t("parentPortal.directory.call_parent", { defaultValue: "Call" })}>
            <FiPhone className="h-4 w-4" />
          </a>
        )}
        {actionAllowed("MAP", hasAddress) && (
          <button type="button" onClick={() => openMap(addressText)} className={iconButtonClass} aria-label={t("parentPortal.directory.open_map_short", { defaultValue: "Map" })}>
            <FiMapPin className="h-4 w-4" />
          </button>
        )}
        {canAdd && (
          <button type="button" onClick={() => downloadVCard(card, { contextChildName })} className={iconButtonClass} aria-label={t("parentPortal.directory.add_contact", { defaultValue: "Add contact" })}>
            <FiUserPlus className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  const renderContactCard = (card, index) => {
    const displayName = card.displayName || t("common.unknown", { defaultValue: "Unknown" });
    const addressText = getAddressText(card);
    const contactKey = buildContactKey(card, index);
    const isExpanded = expandedContactKey === contactKey;
    const isChild = card.type === "CHILD";
    const isTeacher = card.type === "TEACHER";
    const parentContacts = Array.isArray(card.parents) ? card.parents : [];
    const hasParentContacts = parentContacts.length > 0;
    const hasParents = hasParentContacts || (Array.isArray(card.parentNames) && card.parentNames.length > 0);
    const typeLabel = isChild
      ? t("parentPortal.directory.result_child", { defaultValue: "Child" })
      : isTeacher
        ? t("parentPortal.directory.result_teacher", { defaultValue: "Teacher" })
        : t("parentPortal.directory.result_parent", { defaultValue: "Parent" });
    const subtitle = (isChild || isTeacher) && card.classroomName ? `${typeLabel} • ${card.classroomName}` : typeLabel;

    return (
      <li
        key={contactKey}
        className="rounded-[1.25rem] border border-slate-100 bg-white/90 p-3 shadow-sm transition duration-300 motion-safe:animate-[directoryItemIn_280ms_cubic-bezier(0.22,1,0.36,1)_both] hover:-translate-y-0.5 hover:shadow-md dark:border-slate-700/80 dark:bg-slate-900/80"
        style={{ animationDelay: `${Math.min(index * 45, 220)}ms` }}
      >
        <button
          type="button"
          className="flex w-full items-center gap-3 text-left"
          onClick={() => setExpandedContactKey((current) => (current === contactKey ? null : contactKey))}
          aria-expanded={isExpanded}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-base font-extrabold text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/25">
            {getInitials(displayName)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center">
              <span className="truncate text-base font-extrabold text-slate-950 dark:text-white">{displayName}</span>
              {isTeacher && (
                <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-sky-50 px-2 py-0.5 align-middle text-[11px] font-bold text-sky-700 ring-1 ring-sky-100 dark:bg-sky-500/15 dark:text-sky-200 dark:ring-sky-500/25" data-label="teacher">
                {t("parentPortal.directory.teacher_label", { defaultValue: "Teacher" })}
              </span>
              )}
            </span>
            <span className="mt-1 block truncate text-sm font-semibold text-slate-500 dark:text-slate-300">{subtitle}</span>
          </span>
          <FiChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </button>

        {isExpanded && (
          <div className="mt-1.5 grid gap-3 border-t border-slate-100 pt-3 motion-safe:animate-[directoryExpandIn_240ms_cubic-bezier(0.22,1,0.36,1)_both] dark:border-slate-800">
            <div className="grid gap-3 text-sm font-medium text-slate-600 dark:text-slate-300 md:grid-cols-2">
              {card.email && (
                <div className="flex items-center gap-2 min-w-0"><FiMail className="h-4 w-4 shrink-0 text-emerald-700" /><span className="truncate">{card.email}</span></div>
              )}
              {card.phoneMobile && (
                <div className="flex items-center gap-2"><FiPhone className="h-4 w-4 shrink-0 text-emerald-700" /><span>{card.phoneMobile}</span></div>
              )}
              {addressText && (
                <div className="flex items-center gap-2 md:col-span-2"><FiMapPin className="h-4 w-4 shrink-0 text-emerald-700" /><span>{addressText}</span></div>
              )}
              {(isChild || isTeacher) && card.classroomName && (
                <div className="flex items-center gap-2"><FiBookOpen className="h-4 w-4 shrink-0 text-emerald-700" /><span>{card.classroomName}</span></div>
              )}
              {isChild && hasParents && !hasParentContacts && (
                <div className="flex items-center gap-2 md:col-span-2"><FiUsers className="h-4 w-4 shrink-0 text-emerald-700" /><span>{card.parentNames.join(", ")}</span></div>
              )}
            </div>

            {isChild && hasParentContacts && (
              <div className="rounded-[1.25rem] bg-slate-50/80 p-3 ring-1 ring-slate-100 dark:bg-slate-950/30 dark:ring-slate-800">
                <div className="mb-2 text-xs font-extrabold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t("parentPortal.directory.parents", { defaultValue: "Parents" })}
                </div>
                <div className="space-y-2">
                  {parentContacts.map((parent, parentIndex) => {
                    const parentDisplayName = parent.displayName || t("common.unknown", { defaultValue: "Unknown" });
                    return (
                      <div key={parent.parentId || parent.id || `${parentDisplayName}-${parentIndex}`} className="rounded-2xl bg-white p-3 ring-1 ring-slate-100 dark:bg-slate-900 dark:ring-slate-800">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-sm font-extrabold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200">
                            {getInitials(parentDisplayName)}
                          </span>
                          <span className="min-w-0 flex-1 font-bold text-slate-900 dark:text-white">{parentDisplayName}</span>
                          {renderActionButtons(parent, { contextChildName: displayName })}
                        </div>

                        {(parent.email || parent.phoneMobile || getAddressText(parent)) && (
                          <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 text-sm font-medium text-slate-600 dark:border-slate-800 dark:text-slate-300 md:grid-cols-2">
                            {parent.email && (
                              <div className="flex min-w-0 items-center gap-2">
                                <FiMail className="h-4 w-4 shrink-0 text-emerald-700" />
                                <span className="truncate">{parent.email}</span>
                              </div>
                            )}
                            {parent.phoneMobile && (
                              <div className="flex items-center gap-2">
                                <FiPhone className="h-4 w-4 shrink-0 text-emerald-700" />
                                <span>{parent.phoneMobile}</span>
                              </div>
                            )}
                            {getAddressText(parent) && (
                              <div className="flex items-center gap-2 md:col-span-2">
                                <FiMapPin className="h-4 w-4 shrink-0 text-emerald-700" />
                                <span>{getAddressText(parent)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!isChild && renderActionButtons(card)}
          </div>
        )}
      </li>
    );
  };

  const renderSettings = () => {
    const fields = [
      ["displayName", t("parentPortal.name", { defaultValue: "Name" }), FiUser],
      ["email", t("parentPortal.email", { defaultValue: "Email" }), FiMail],
      ["phoneMobile", t("parentPortal.phone_mobile", { defaultValue: "Mobile" }), FiPhone],
      ["phoneOther", t("parentPortal.phone_other", { defaultValue: "Phone (other)" }), FiPhone],
      ["addressStreet", t("parentPortal.address", { defaultValue: "Address" }), FiMapPin],
      ["addressPostalCode", t("parentPortal.zipcode", { defaultValue: "Postal code" }), FiMapPin],
      ["addressCity", t("parentPortal.city", { defaultValue: "City" }), FiMapPin],
    ];

    return (
      <section className="rounded-[1.25rem] border border-slate-100 bg-white/85 p-4 shadow-sm motion-safe:animate-[directorySlideIn_420ms_cubic-bezier(0.22,1,0.36,1)_both] dark:border-slate-700/80 dark:bg-slate-900/80">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-xl font-black tracking-tight text-slate-950 dark:text-white">
              {t("parentPortal.directory.share_title", { defaultValue: "Directory settings" })}
            </h3>
            <p className="m-0 mt-1.5 text-base font-medium text-slate-500 dark:text-slate-300">
              {t("parentPortal.directory.share_intro", {
                defaultValue: "Choose which details you want to share with other parents.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="rounded-2xl bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm ring-1 ring-slate-100 transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
            onClick={() => setDirSettingsOpen(false)}
            disabled={myDirSaving}
          >
            {t("common.back", { defaultValue: "Back" })}
          </button>
        </div>

        <div className="overflow-hidden rounded-[1.25rem] bg-white shadow-sm ring-1 ring-slate-100 dark:bg-slate-950/30 dark:ring-slate-800">
          {fields.map(([key, label, Icon], index) => (
            <div key={key} className={`flex items-center gap-3 px-4 py-3 ${index > 0 ? "border-t border-slate-100 dark:border-slate-800" : ""}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-extrabold text-slate-950 dark:text-slate-50">{label}</div>
                <div className="truncate text-sm font-medium text-slate-500 dark:text-slate-400">
                  {previewValues?.[key] || t("common.none", { defaultValue: "Not filled in" })}
                </div>
              </div>
              <EmeraldSwitch checked={!!share?.[key]} onChange={(val) => setShare((current) => ({ ...current, [key]: val }))} disabled={myDirSaving} />
            </div>
          ))}
        </div>

        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-3">
          <TextLink onClick={turnOffListing} disabled={myDirSaving}>
            {t("parentPortal.directory.turn_off", { defaultValue: "Turn off listing" })}
          </TextLink>
          <div className="flex gap-3">
            <button type="button" className="rounded-2xl bg-white px-4 py-2.5 text-sm font-bold text-slate-700 shadow-sm ring-1 ring-slate-100 transition hover:bg-slate-50 disabled:opacity-60 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700" onClick={() => setDirSettingsOpen(false)} disabled={myDirSaving}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button type="button" className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700 disabled:opacity-60" onClick={saveDirectorySettings} disabled={myDirSaving}>
              {myDirSaving ? t("common.saving", { defaultValue: "Saving..." }) : t("common.save", { defaultValue: "Save" })}
            </button>
          </div>
        </div>

        {myDirErr && <p className="mt-1.5 rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 ring-1 ring-red-100 dark:bg-red-950/40 dark:text-red-200 dark:ring-red-800">{myDirErr}</p>}
      </section>
    );
  };

  return (
    <div className="space-y-2.5 text-left text-slate-950 motion-safe:animate-[directorySlideIn_420ms_cubic-bezier(0.22,1,0.36,1)_both] dark:text-slate-50">
      {dirSettingsOpen ? (
        renderSettings()
      ) : (
        <>
          <header className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50">
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm"
              aria-hidden="true"
            >
              <FiBookOpen className="h-5 w-5" />
            </span>

            <span className="min-w-0 flex-1">
              <h2 className="m-0 text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
                {directoryTitle}
              </h2>
              <p className="m-0 mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                {t("parentPortal.directory.subtitle", {
                  defaultValue: "Search parents and children within the school.",
                })}
              </p>
            </span>

            {canViewDirectory && (
              <button
                type="button"
                onClick={openDirSettings}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-slate-700 shadow-sm ring-1 ring-slate-200 transition duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-md dark:bg-slate-950/40 dark:text-slate-200 dark:ring-slate-700"
                aria-label={t("parentPortal.directory.edit_sharing", { defaultValue: "Settings" })}
              >
                <FiSettings className="h-5 w-5" />
              </button>
            )}
          </header>

          {!canViewDirectory ? (
            <section className="rounded-[1.25rem] border border-orange-100 bg-orange-50/80 p-4 text-orange-900 shadow-sm dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-100">
              <h4 className="m-0 text-base font-extrabold">
                {t("parentPortal.directory.not_listed_title", { defaultValue: "You are not listed in the directory yet." })}
              </h4>
              <p className="mt-1.5 text-sm font-medium">
                {t("parentPortal.directory.require_publish", {
                  defaultValue: "When you share your details, you can also search other parents.",
                })}
              </p>
              <button type="button" onClick={openDirSettings} className="mt-1.5 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700">
                {t("parentPortal.directory.set_profile", { defaultValue: "Share details" })}
              </button>
            </section>
          ) : (
            <>
              <section className="rounded-[1.25rem] border border-slate-100 bg-white/85 p-3 shadow-sm dark:border-slate-700/80 dark:bg-slate-900/80" aria-label={t("parentPortal.directory.search", { defaultValue: "Search directory" })}>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitDirectorySearch();
                  }}
                >
                  <div className="relative">
                    <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
                    <input
                      ref={dirInputRef}
                      type="text"
                      className="w-full rounded-[1.25rem] border border-slate-200 bg-slate-50/70 py-3 pl-11 pr-20 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-emerald-500/60 dark:focus:bg-slate-900 dark:focus:ring-emerald-500/20"
                      placeholder={searchPlaceholder}
                      value={dirQ}
                      onChange={(event) => setDirQ(event.target.value)}
                      disabled={dirIsLoading}
                    />
                    {dirQ && !dirIsLoading && (
                      <button
                        type="button"
                        onClick={() => {
                          setDirQ("");
                          setUnifiedData(null);
                          setDirData?.(null);
                          setUnifiedError(null);
                          setExpandedContactKey(null);
                          dirInputRef.current?.focus();
                        }}
                        className="absolute inset-y-0 right-14 flex items-center justify-center text-slate-400 transition hover:text-emerald-700 dark:hover:text-emerald-300"
                        aria-label={t("common.clear", { defaultValue: "Clear" })}
                      >
                        <FiX className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="submit"
                      className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-700 disabled:opacity-60"
                      disabled={dirIsLoading}
                      aria-label={t("common.search", { defaultValue: "Search" })}
                    >
                      <FiSearch className="h-4 w-4" />
                    </button>
                  </div>
                </form>
              </section>

              <section className="space-y-2.5">
                <div className="flex items-center justify-between gap-3 px-2">
                  <h4 className="m-0 text-xl font-black tracking-tight text-slate-950 dark:text-white">
                    {hasResults
                      ? t("parentPortal.directory.results_heading_unified", { defaultValue: "Found results" })
                      : t("parentPortal.directory.search_heading", { defaultValue: "Search" })}
                  </h4>
                  {hasResults && <span className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-extrabold text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-200 dark:ring-emerald-500/25">{totalElements}</span>}
                </div>

                {dirIsLoading && <div className="rounded-[1.25rem] border border-slate-100 bg-white/80 p-5 text-sm font-semibold text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300">{t("common.loading", { defaultValue: "Loading..." })}</div>}
                {!dirIsLoading && unifiedError && <div className="rounded-[1.25rem] border border-red-100 bg-red-50 p-5 text-sm font-semibold text-red-700 shadow-sm dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">{unifiedError}</div>}
                {!dirIsLoading && unifiedData == null && (
                  <div className="rounded-[1.25rem] border border-dashed border-slate-200 bg-white/60 p-5 text-sm font-semibold text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-300">
                    {t("parentPortal.directory.search_hint", {
                      defaultValue: "Search a parent or child to view the directory.",
                    })}
                  </div>
                )}
                {!dirIsLoading && unifiedData && !hasResults && (
                  <div className="rounded-[1.25rem] border border-orange-100 bg-orange-50 p-5 text-sm font-semibold text-orange-800 shadow-sm dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-100">
                    {t("parentPortal.directory.no_results", { defaultValue: "No results found." })}
                  </div>
                )}
                {!dirIsLoading && hasResults && <ul className="space-y-2.5">{content.map(renderContactCard)}</ul>}
              </section>
            </>
          )}
        </>
      )}

      <style>{`
        @keyframes directorySlideIn {
          from { opacity: 0; transform: translateX(32px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes directoryItemIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes directoryExpandIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default DirectoryTab;
